/*
 * Completa el look — Mejoras UX Rivera (punto 5, septiembre 2026)
 *
 * Rellena los huecos [data-completa-look] (snippets/completa-el-look-hueco.liquid) de la página del carrito y
 * del carrito lateral con hasta 2 complementarios de Search & Discovery:
 *  - prendas de origen: los productos del carrito en el orden de /cart.js (la línea más nueva primero, medido
 *    el 28-sep-2026), sin el sobre de regalo, los de 0 € ni las tarjetas regalo; hasta las que diga el ajuste
 *    «garments to check» (data-origenes, de 1 a 10);
 *  - de cada una, sus complementarios (/recommendations/products.json con intent=complementary), en el orden
 *    configurado. Se juntan prenda a prenda, sin repetidos, sin agotados y sin lo que ya está en el carrito,
 *    y se quedan 2;
 *  - en el cajón no se pinta si alguna lista contiene otra prenda del carrito («el look ya está completo»),
 *    salvo que el ajuste lo desactive (data-ocultar-relacionadas="false").
 * La posición del hueco la decide el Liquid (snippets/completa-el-look-hueco.liquid), con un ajuste para cada
 * carrito. En la página del carrito, antes o después del envoltorio, el hueco va dentro de [js-cart-items], que
 * refreshCart de Stiletto sustituye: lo repone el mismo MutationObserver que el del cajón.
 * Las tarjetas son las de los listados de Stiletto (product-item), y en esa columna miden lo que una de colección.
 * Las listas se piden sin cookies: con la del carrito, Shopify quita de la lista lo que ya está en él (medido
 * el 28-sep-2026), y entonces ni la caché valdría ni se verían las prendas relacionadas. El JSON no depende
 * del tema, así que funciona igual en la preview y en el publicado. Las tarjetas se pintan aquí; los textos,
 * en el idioma de la página, llegan en el hueco.
 * Caché en sessionStorage por producto, idioma, país y moneda, 5 minutos; tras un error o un 429 esa clave
 * espera 60 s, y al acabar la espera se recalcula solo (hasta 3 veces seguidas). Stiletto repinta los
 * carritos con innerHTML: un MutationObserver repone el bloque al momento si el carrito no ha cambiado, y lo
 * recalcula si cambió.
 * Todas las peticiones van por window.fetch, leído en cada llamada, para poder simularlas en las pruebas.
 * Spec: _SPECS/eduardo-rivera/005-completa-el-look.
 */
(() => {
  const SEL = {
    hueco: '[data-completa-look]',
    raices: '.quick-cart, [data-section-type="cart"]',
    lineas: '.quick-cart [data-input-item], [data-section-type="cart"] [data-input-item]',
    talla: '[data-completa-look] [data-look-talla]',
    anadir: '[data-completa-look] [data-look-anadir]',
  };
  const MAXIMO = 2;
  const ORIGENES = 10; // si el hueco no trae data-origenes
  const CADUCA = 5 * 60 * 1000;
  const ESPERA_ERROR = 60 * 1000;
  const PREFIJO = 'rivera:look:';

  const rutas = () => (window.theme && window.theme.routes) || {};
  const rutaCarrito = () => (rutas().cart && rutas().cart.base) || '/cart';
  const rutaRecomendaciones = () => rutas().productRecommendations || '/recommendations/products';
  const raizTienda = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const hayBus = () => typeof window.r$1 === 'function';
  const escapar = texto =>
    String(texto == null ? '' : texto).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  // --- Precio: el formato de la tienda, sin ceros finales (como money_without_trailing_zeros) ------------
  function dinero(centimos) {
    const formato = String((window.theme && window.theme.moneyFormat) || '{{amount_with_comma_separator}}€').replace(/<[^>]*>/g, '');
    const valor = Number(centimos || 0) / 100;
    const cifra = (decimales, miles, separador) => {
      const [entero, fraccion] = valor.toFixed(decimales).split('.');
      const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
      return fraccion ? `${conMiles}${separador}${fraccion}` : conMiles;
    };
    const texto = formato.replace(/\{\{\s*(\w+)\s*\}\}/, (_, tipo) => {
      switch (tipo) {
        case 'amount_no_decimals':
          return cifra(0, ',', '.');
        case 'amount_with_comma_separator':
          return cifra(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return cifra(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return cifra(2, "'", '.');
        case 'amount_with_space_separator':
          return cifra(2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return cifra(2, ' ', '.');
        case 'amount_no_decimals_with_space_separator':
          return cifra(0, ' ', '');
        default:
          return cifra(2, ',', '.');
      }
    });
    return texto.replace(/([.,])00(?!\d)/, '');
  }

  const imagenUrl = (url, ancho) => {
    if (!url) return '';
    const absoluta = url.indexOf('//') === 0 ? `https:${url}` : url;
    return `${absoluta}${absoluta.indexOf('?') === -1 ? '?' : '&'}width=${ancho}`;
  };

  // --- Caché de listas por producto, idioma, país y moneda ---------------------------------------------
  const enVuelo = new Map();
  const bloqueadas = new Map();

  function claveDe(id) {
    const s = window.Shopify || {};
    return [PREFIJO + id, s.locale || '', s.country || '', (s.currency && s.currency.active) || ''].join('|');
  }
  function leerCache(clave) {
    try {
      const guardado = JSON.parse(sessionStorage.getItem(clave) || 'null');
      if (guardado && Date.now() - guardado.t < CADUCA) return guardado.lista;
    } catch (e) {
      // Sin almacenamiento: se pide cada vez.
    }
    return null;
  }
  function guardarCache(clave, lista) {
    try {
      sessionStorage.setItem(clave, JSON.stringify({ t: Date.now(), lista }));
    } catch (e) {
      // Cuota llena o navegación privada: se sigue sin caché.
    }
  }
  function vaciarCache() {
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith(PREFIJO))
        .forEach(k => sessionStorage.removeItem(k));
    } catch (e) {
      // Nada que vaciar.
    }
    enVuelo.clear();
    bloqueadas.clear();
  }

  // Lista completa de complementarios de un producto, en el orden de Search & Discovery, o null si falla.
  function listaDe(id) {
    const clave = claveDe(id);
    const guardada = leerCache(clave);
    if (guardada) return Promise.resolve(guardada);
    if ((bloqueadas.get(clave) || 0) > Date.now()) return Promise.resolve(null);
    if (enVuelo.has(clave)) return enVuelo.get(clave);

    const url = `${rutaRecomendaciones()}.json?product_id=${encodeURIComponent(id)}&intent=complementary&limit=10`;
    const peticion = window
      .fetch(url, { credentials: 'omit' })
      .then(r => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(datos => {
        const lista = (datos.products || [])
          .filter(p => !p.gift_card)
          .map(p => ({
            id: String(p.id),
            disponible: !!p.available,
            handle: p.handle,
            titulo: p.title,
            marca: p.vendor || '',
            imagen: p.featured_image || (p.images && p.images[0]) || '',
            precio: p.price,
            compara: p.compare_at_price || 0,
            varia: !!p.price_varies,
            variantes: (p.variants || []).length,
            variante: p.variants && p.variants[0] ? p.variants[0].id : null,
          }));
        guardarCache(clave, lista);
        return lista;
      })
      .catch(() => {
        bloqueadas.set(clave, Date.now() + ESPERA_ERROR);
        return null;
      })
      .finally(() => enVuelo.delete(clave));
    enVuelo.set(clave, peticion);
    return peticion;
  }

  // --- Tarjetas ------------------------------------------------------------------------------------------
  function textosDe(hueco) {
    try {
      return JSON.parse(hueco.dataset.textos || '{}');
    } catch (e) {
      return {};
    }
  }

  // La tarjeta es la de los listados de Stiletto (snippets/product-item.liquid): mismas clases, así que toma sus
  // estilos y sus ajustes (proporción y ajuste de la imagen, marca, tipografía del título), con el precio sin
  // código de moneda, como en las colecciones. No lleva lo que no aplica en el carrito: etiquetas, segunda
  // imagen al pasar el ratón, lista de deseos ni animaciones de entrada.
  // Mismo patrón que product-item y product-card-mini: [preorder-product-element][d-id] y
  // [data-quick-shop-trigger="quick-view"], para que Globo y la talla obligatoria (tarea 1) preparen la
  // pre-venta en la vista rápida.
  function tarjetaHtml(t, textos, ficha) {
    const url = `${raizTienda()}products/${t.handle}`;
    let precio;
    if (t.compara > t.precio) {
      precio =
        `<span class="visually-hidden">${escapar(textos.normal)}</span><s class="t-subdued">${escapar(dinero(t.compara))}</s> ` +
        `<span class="visually-hidden">${escapar(textos.rebajado)}</span><span class="sale">${escapar(dinero(t.precio))}</span>`;
    } else if (t.varia && textos.desde) {
      // La traducción de Stiletto trae HTML propio; solo el precio va escapado.
      precio = textos.desde.replace('__PRECIO__', escapar(dinero(t.precio)));
    } else {
      precio = escapar(dinero(t.precio));
    }
    const boton =
      t.variantes === 1 && t.variante
        ? `<button type="button" class="completa-look__boton btn btn--tertiary btn--x-small" data-look-anadir="${escapar(t.variante)}"><span>${escapar(textos.anadir)}</span><div class="btn__loading-wrap"><div class="btn__loading-bar"></div></div></button>`
        : `<button type="button" class="completa-look__boton btn btn--tertiary btn--x-small" data-quick-shop-trigger="quick-view" data-product-url="${escapar(url)}" data-look-talla><span>${escapar(textos.talla)}</span></button>`;
    const aspecto = ficha.aspecto && ficha.aspecto !== 'natural' ? ` aspect-ratio--${escapar(ficha.aspecto)}` : '';
    const ajuste = aspecto && ficha.ajuste ? ` style="--object-fit: ${escapar(ficha.ajuste)};"` : '';
    const imagen = t.imagen
      ? `<img src="${escapar(imagenUrl(t.imagen, 540))}" srcset="${[360, 540, 720, 900].map(w => `${escapar(imagenUrl(t.imagen, w))} ${w}w`).join(', ')}" sizes="(min-width: 720px) 320px, 50vw" alt="" loading="lazy" width="540" height="810">`
      : '<span class="completa-look__sin-imagen"></span>';
    const marca = ficha.marca && t.marca ? `<h5 class="ff-body fs-body-60 product-item__product-vendor">${escapar(t.marca)}</h5>` : '';
    return (
      `<li class="completa-look__item" data-look-tarjeta data-product-id="${escapar(t.id)}">` +
      `<div class="product-item completa-look__tarjeta" preorder-product-element="form" d-id="${escapar(t.id)}" d-handle="${escapar(t.handle)}">` +
      `<div class="product-item__inner">` +
      `<div class="product-item__media"><a class="product-item__image-link" href="${escapar(url)}" tabindex="-1" aria-hidden="true">` +
      `<div class="image product-item__image${aspecto}"${ajuste}>${imagen}</div></a></div>` +
      `<div class="product-item__meta"><div class="product-item__text ta-c">` +
      `<h4 class="ff-body product-item__product-title fs-product-card-title"><a href="${escapar(url)}">${escapar(t.titulo)}</a></h4>` +
      marca +
      `<p class="product-item__price">${precio}</p>` +
      `</div>${boton}</div>` +
      `</div></div></li>`
    );
  }

  function bloqueHtml(hueco, elegidas) {
    const id = `CompletaLook-${hueco.dataset.completaLook}`;
    const textos = textosDe(hueco);
    const ficha = { aspecto: hueco.dataset.aspecto, ajuste: hueco.dataset.ajusteImagen, marca: hueco.dataset.marca === 'true' };
    return (
      `<section class="completa-look__bloque" aria-labelledby="${id}">` +
      `<h3 id="${id}" class="completa-look__titulo ff-heading fs-heading-6-base">${escapar(hueco.dataset.titulo)}</h3>` +
      `<ul class="completa-look__lista" role="list">${elegidas.map(t => tarjetaHtml(t, textos, ficha)).join('')}</ul>` +
      `</section>`
    );
  }

  // --- Ancho de las tarjetas en la columna de las prendas de /cart ---------------------------------------
  // Mismo ancho que una tarjeta de colección: desde 720 la rejilla de Stiletto (theme.css, .collection__products)
  // reparte el ancho de la página en 4 columnas (las de la colección en escritorio) con un hueco de --spacing-4 +
  // 1 px, y de --spacing-6 + 1 px desde 1200. Por debajo de 720 la colección va a 2 columnas, como el bloque.
  const COLUMNAS_COLECCION = 4;
  // Las variables de espaciado de Stiletto son calc(): se miden con un elemento en lugar de leerlas como número.
  function espaciado(variable) {
    const medidor = document.createElement('div');
    medidor.style.cssText = `position:absolute;visibility:hidden;width:var(${variable});height:0`;
    document.body.appendChild(medidor);
    const valor = medidor.getBoundingClientRect().width;
    medidor.remove();
    return valor;
  }
  function anchoFichas() {
    const escritorio = window.matchMedia('(min-width: 720px)').matches;
    document.querySelectorAll('[data-section-type="cart"] .cart__form-items [data-completa-look="pagina"]').forEach(hueco => {
      const pagina = hueco.closest('.cart__container');
      if (!escritorio || !pagina) {
        hueco.style.removeProperty('--completa-look-ficha');
        hueco.style.removeProperty('--completa-look-hueco');
        return;
      }
      const separacion = espaciado(window.matchMedia('(min-width: 1200px)').matches ? '--spacing-6' : '--spacing-4') + 1;
      const cs = getComputedStyle(pagina);
      const ancho = pagina.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const ficha = (ancho - (COLUMNAS_COLECCION - 1) * separacion) / COLUMNAS_COLECCION;
      hueco.style.setProperty('--completa-look-ficha', `${ficha.toFixed(2)}px`);
      hueco.style.setProperty('--completa-look-hueco', `${separacion}px`);
    });
  }

  // --- Cálculo y pintado ------------------------------------------------------------------------------
  let estado = null; // { elegidas, relacionadas }
  let firmaCalculada = null;
  let turno = 0;

  // Las líneas del carrito en el DOM y su orden: cambian al añadir, quitar o cambiar de talla (la clave de
  // línea). El orden cuenta, porque decide las prendas de origen.
  const firmaDelDom = () =>
    [...new Set([...document.querySelectorAll(SEL.lineas)].map(l => l.dataset.key || ''))].join(',');

  function pintar() {
    document.querySelectorAll(SEL.hueco).forEach(hueco => {
      const ocultable = hueco.dataset.completaLook === 'cajon' && hueco.dataset.ocultarRelacionadas !== 'false';
      const ver = !!(estado && estado.elegidas.length && !(ocultable && estado.relacionadas));
      // Lo que se ve: si cambia un precio o una imagen al caducar la caché, también se repinta.
      const firma = ver
        ? estado.elegidas.map(t => [t.id, t.precio, t.compara, t.varia ? 1 : 0, t.imagen ? 1 : 0, t.titulo].join(':')).join(',')
        : 'vacio';
      if (hueco.dataset.firma === firma) return;
      hueco.dataset.firma = firma;
      hueco.innerHTML = ver ? bloqueHtml(hueco, estado.elegidas) : '';
      hueco.hidden = !ver;
    });
    // El hueco de la columna de las prendas es nuevo tras cada repintado de Stiletto: se le vuelve a dar el ancho.
    anchoFichas();
  }

  async function calcular() {
    const huecos = document.querySelectorAll(SEL.hueco);
    if (!huecos.length) return;
    const miTurno = ++turno;
    const firma = firmaDelDom();
    const listo = nuevo => {
      if (miTurno !== turno) return;
      firmaCalculada = firma;
      estado = nuevo;
      pintar();
    };
    if (!firma) return listo(null);

    const excluidos = new Set((huecos[0].dataset.excluidos || '').split(',').filter(Boolean));
    const maxOrigenes = Math.min(10, Math.max(1, parseInt(huecos[0].dataset.origenes, 10) || ORIGENES));
    let cart = null;
    try {
      cart = await window.fetch(`${rutaCarrito()}.js`, { credentials: 'same-origin' }).then(r => r.json());
    } catch (e) {
      cart = null;
    }
    if (!cart || !Array.isArray(cart.items)) return listo(null);

    const enCarrito = new Set(cart.items.map(i => String(i.product_id)));
    const origenes = [];
    for (const item of cart.items) {
      const id = String(item.product_id);
      if (excluidos.has(id) || item.gift_card || item.original_price === 0 || origenes.includes(id)) continue;
      origenes.push(id);
      if (origenes.length === maxOrigenes) break;
    }
    if (!origenes.length) return listo(null);

    const listas = await Promise.all(origenes.map(listaDe));
    // Si falla alguna lista (error o 429), no hay bloque: mejor nada que una recomendación a medias. Al acabar
    // la espera de esa clave se recalcula solo, sin esperar a que cambie el carrito.
    if (listas.some(lista => lista === null)) {
      if (miTurno === turno) reintentarTrasEspera();
      return listo(null);
    }

    const elegidas = [];
    const vistas = new Set();
    let relacionadas = false;
    listas.forEach(lista =>
      lista.forEach(t => {
        if (enCarrito.has(t.id)) {
          if (!excluidos.has(t.id)) relacionadas = true;
          return;
        }
        if (vistas.has(t.id) || !t.disponible || excluidos.has(t.id)) return;
        vistas.add(t.id);
        if (elegidas.length < MAXIMO) elegidas.push(t);
      })
    );
    reintentos = 0;
    listo({ elegidas, relacionadas });
  }

  // --- Repintados de Stiletto --------------------------------------------------------------------------
  let espera;
  const programar = () => {
    clearTimeout(espera);
    espera = setTimeout(calcular, 150);
  };
  // Un solo reintento pendiente: cuando caduca la espera más larga de las claves bloqueadas. Como mucho 3
  // seguidos sin un cálculo correcto, para no quedarse pidiendo cada minuto si la API sigue fallando.
  let reintento;
  let reintentos = 0;
  function reintentarTrasEspera() {
    clearTimeout(reintento);
    if (reintentos >= 3) return;
    reintentos += 1;
    const hasta = Math.max(Date.now(), ...bloqueadas.values());
    reintento = setTimeout(() => {
      firmaCalculada = null;
      calcular();
    }, hasta - Date.now() + 200);
  }
  function alMutar() {
    if (!document.querySelector(SEL.hueco)) return;
    // Lo que se veía se repone ya, en la misma microtarea y sin peticiones, también si el carrito cambió (otra
    // talla de la misma prenda, una prenda menos): así el bloque no desaparece mientras se recalcula, y después
    // solo cambia si hace falta. Con el carrito vacío no se repone nada.
    const firma = firmaDelDom();
    if (firma) pintar();
    if (firma !== firmaCalculada) programar();
    repararFoco();
  }
  const observadas = new WeakSet();
  function observar() {
    document.querySelectorAll(SEL.raices).forEach(raiz => {
      if (observadas.has(raiz)) return;
      observadas.add(raiz);
      new MutationObserver(alMutar).observe(raiz, { childList: true, subtree: true });
    });
  }

  // --- Botones -----------------------------------------------------------------------------------------
  let botonVistaRapida = null;

  async function anadirDirecto(boton) {
    if (boton.classList.contains('loading')) return;
    boton.classList.add('loading');
    try {
      const respuesta = await window.fetch(`${rutaCarrito()}/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(boton.dataset.lookAnadir), quantity: 1 }] }),
      });
      if (!respuesta.ok) throw new Error(String(respuesta.status));
      if (!hayBus()) {
        window.location.reload();
        return;
      }
      const cart = await window.fetch(`${rutaCarrito()}.js`, { credentials: 'same-origin' }).then(r => r.json());
      // Lo mismo que hace Stiletto al añadir: repinta los dos carritos y abre el lateral.
      window.r$1('cart:updated', { cart });
      window.r$1('quick-cart:updated');
      setTimeout(() => window.r$1('quick-cart:open'), 300);
    } catch (e) {
      boton.classList.remove('loading');
    }
  }

  document.addEventListener('click', e => {
    const talla = e.target.closest(SEL.talla);
    if (talla) {
      e.preventDefault();
      botonVistaRapida = talla;
      // Vista rápida sin talla preseleccionada (tarea 1).
      if (hayBus()) window.r$1('quick-view:open', null, { productUrl: talla.dataset.productUrl });
      else window.location.href = talla.dataset.productUrl;
      return;
    }
    const anadir = e.target.closest(SEL.anadir);
    if (anadir) {
      e.preventDefault();
      anadirDirecto(anadir);
    }
  });

  // Al cerrar la vista rápida, el foco vuelve al botón que la abrió. Stiletto aún lo mueve después: con Escape
  // en /cart la trampa de foco lo deja en body, y si se añadió la prenda el carrito se repinta y el botón
  // desaparece. Durante unos segundos, si el foco se pierde, se devuelve al botón o, si ya no está y el cajón
  // está abierto, a su botón de cerrar, que es donde lo deja Stiletto al abrir el cajón tras añadir. Un clic
  // con el ratón lo cancela.
  let focoPendiente = null; // { boton, hasta }
  function repararFoco() {
    if (!focoPendiente) return;
    if (Date.now() > focoPendiente.hasta) {
      focoPendiente = null;
      return;
    }
    const activo = document.activeElement;
    if (activo && activo !== document.body) return;
    const cajon = document.querySelector('.quick-cart__wrapper.active');
    const destino = focoPendiente.boton.isConnected
      ? focoPendiente.boton
      : cajon && cajon.querySelector('.quick-cart__close-icon');
    if (destino) destino.focus({ preventScroll: true });
  }
  new MutationObserver(() => {
    if (!botonVistaRapida || document.body.getAttribute('data-fluorescent-overlay-open') === 'true') return;
    focoPendiente = { boton: botonVistaRapida, hasta: Date.now() + 5000 };
    botonVistaRapida = null;
    if (focoPendiente.boton.isConnected) focoPendiente.boton.focus({ preventScroll: true });
    setTimeout(repararFoco, 600);
  }).observe(document.body, { attributes: true, attributeFilter: ['data-fluorescent-overlay-open'] });
  document.addEventListener('focusout', e => {
    if (focoPendiente && !e.relatedTarget) setTimeout(repararFoco, 0);
  });
  document.addEventListener(
    'pointerdown',
    () => {
      focoPendiente = null;
    },
    true
  );

  document.addEventListener('shopify:section:load', () => {
    observar();
    programar();
  });
  window.addEventListener('pageshow', e => {
    if (e.persisted) programar();
  });
  let esperaAncho;
  window.addEventListener('resize', () => {
    clearTimeout(esperaAncho);
    esperaAncho = setTimeout(anchoFichas, 150);
  });

  // Para las pruebas (spec 005, RF-7).
  window.RiveraCompletaElLook = {
    recalcular: () => {
      firmaCalculada = null;
      return calcular();
    },
    vaciarCache,
  };

  observar();
  calcular();
})();
